// @flow

/**
 * Lightweight metafunction for throttling high-frequency callbacks
 * Ensures that a function is only called once per ${delay} millis. If additional calls are made during this window,
 * a single call will be queued until after the window expires. If `true` is passed to the `swallow` param, additional
 * calls will simply be swallowed.
 *
 * Don't call `throttle` multiple times. Create a single throttled function and reuse it.
 *
 * Incorrect:
 *
 *   function clickHandler() {
 *     throttle(_internalFunction, 2000)();
 *   }
 *
 * Correct:
 *
 *   const clickHandler = throttle(_internalFunction, 2000);
 *
 * @author mtownsend
 * @since Dec 2016
 *
 * Moved into components by skline, 2018-01-18.
 */

/**
 * Creates a throttled function that will never be executed more than once every ${delay} milliseconds.
 * @param {Function} func - The function to throttle
 * @param {Number} delay - The throttling window, in milliseconds
 * @param {boolean} [swallow] - If set to true, additional calls will be swallowed instead of queued
 */
export default function(func: Function, delay: number, swallow: ?boolean) {
  let last;
  let delayedCB;
  return function throttle(...args: any): mixed {
    if (delayedCB) {
      clearTimeout(delayedCB);
      delayedCB = 0;
    }

    const delta = last ? delay - (Date.now() - last) : null;
    if (delta !== null && delta > 0) {
      if (!swallow) {
        // Swallowing uses of throttle don't queue up final calls. They just swallow extra ones.
        // $FlowFixMe: https://github.com/facebook/flow/issues/5681
        delayedCB = setTimeout(func.apply.bind(func, this, args), delta);
      }
      return null;
    }

    // Let the function finish before saving the date, in case it's slow.
    const rval = func.apply(this, args);
    last = Date.now();
    return rval;
  };
}
